import React, { memo, useCallback, useReducer, useRef, useEffect, useState, useMemo } from 'react';
import { Modal } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { ENTITIES } from 'lib/constants';
import { LOGEVENT_SUBMODULE } from './constants';
import { getMappedFieldList, PARENT_ENTITY_MAP } from './lib';
import { publish } from 'lib/EventBuser';
import { FIELDS_REFRESH } from 'lib/events';
import { getList } from 'lib/services/valueLists';
import { updateTabsOrder } from 'lib/services/fields';
import { TransitionGroup, FadeTransition } from 'components/Transitions';
import FieldsLists from './FieldsLists';
import FormStandardField from './FormStandardField';
import FormExtraField from './FormExtraField';
import FormTab from './FormTab';
import TabsLists from './TabsLists';
import AddOnInfo from 'components/Settings/AddOnInfo';

const reducer = (state, action) => {
    switch (action.type) {
        case 'CHANGE_TAB':
            state = {
                ...state,
                activeTab: action.tab,
                activeShrinkedTab: null,
                entityLogSubmodule: action.entityLogSubmodule,
            };
            return state;
        case 'CHANGE_SHRINKED_TAB':
            state = {
                ...state,
                activeTab: action.value ? action.value.value : 'Empresas',
                activeShrinkedTab: action.value,
            };
            return state;
        case 'SET_FIELDS_LIST_AND_ORDERS':
            state = {
                ...state,
                fieldsData: { ...state.fieldsData, [action.entity]: action.data },
            };
            return state;
        case 'SET_BRANCHES':
            state = { ...state, branches: action.data };
            return state;
        case 'SET_TABS_MODAL':
            state = { ...state, tabsModal: action.data };
            return state;
        case 'SET_EXTRA_ORDER':
            state = {
                ...state,
                fieldsData: {
                    ...state.fieldsData,
                    [action.entity]: {
                        ...state.fieldsData[action.entity],
                        extraOrder: {
                            ...state.fieldsData[action.entity].extraOrder,
                            [action.tabId]: action.extraOrder,
                        },
                    },
                },
            };
            return state;
        case 'SET_TABS_ORDER':
            state = {
                ...state,
                tabsModal: { isOpen: false, order: [] },
                fieldsData: {
                    ...state.fieldsData,
                    [action.entity]: {
                        ...state.fieldsData[action.entity],
                        tabsOrder: action.tabsOrder,
                    },
                },
            };
            return state;
        case 'SET_UPGRADE_INFO_TABS':
            return {
                ...state,
                isUpgradeInfoTabsOpen: action.value,
            };
        case 'SET_UPGRADE_INFO_ADMIN':
            return {
                ...state,
                isUpgradeInfoAdminOpen: action.value,
            };
        default:
            return state;
    }
};

const initialState = {
    activeTab: ENTITIES.ACCOUNTS.fields,
    activeShrinkedTab: null,
    fieldsData: {},
    branches: null,
    tabsModal: { isOpen: false, order: [] },
    entityLogSubmodule: 'accountsTab',
    isUpgradeInfoTabsOpen: false,
    isUpgradeInfoAdminOpen: false,
};

const Fields = ({
    idSetupRole,
    overrides,
    onUpdate,
    contentLiteral,
    licenseEditionCode,
    productCode,
}) => {
    const { t, language, logEvent, customGeneralTab, overrideActiveTab, handleUpgradePlan, isWeb } =
        useWeb5Context({
            overrides,
        });

    const formStandardField = useRef();
    const formExtraField = useRef();
    const formTab = useRef();
    const entityLogName = 'fields';
    const [state, dispatch] = useReducer(reducer, initialState);
    const [activeRowId, setActiveRowId] = useState(null);

    const productCodeIsSage = useMemo(
        () => productCode?.toLowerCase()?.includes('sage'),
        [productCode],
    );

    const licenseEditionCodeIsSage = useMemo(
        () => licenseEditionCode?.toLowerCase()?.includes('sage'),
        [licenseEditionCode],
    );

    useEffect(() => {
        if (state.branches) return;
        getList({ list: 'tblSucursales', language })
            .then(({ data }) => {
                data = data.map((branch) => ({
                    value: branch.id,
                    label: branch.description,
                }));
                dispatch({
                    type: 'SET_BRANCHES',
                    data,
                });
            })
            .catch((err) => console.warn(err));
    }, [language, state.branches]);

    const entity = useMemo(
        () => state.activeTab || (state.activeShrinkedTab ? state.activeShrinkedTab.value : null),
        [state.activeShrinkedTab, state.activeTab],
    );

    const onChangeTab = useCallback(
        (tab) => {
            const submodule = LOGEVENT_SUBMODULE[tab] ? LOGEVENT_SUBMODULE[tab] : 'otherTablesTab';
            logEvent &&
                logEvent({
                    event: entityLogName,
                    submodule: submodule,
                    functionality: 'listView',
                });

            dispatch({ type: 'CHANGE_TAB', tab, entityLogSubmodule: submodule });
        },
        [logEvent],
    );

    const onChangeShrinked = useCallback(
        (value) => dispatch({ type: 'CHANGE_SHRINKED_TAB', value }),
        [dispatch],
    );

    const getEntityData = useCallback(
        (entity) => {
            return new Promise((resolve, reject) => {
                getMappedFieldList(entity, language, t, customGeneralTab)
                    .then((data) => {
                        if (!data) {
                            reject();
                        } else {
                            dispatch({
                                type: 'SET_FIELDS_LIST_AND_ORDERS',
                                entity,
                                data,
                            });
                            resolve(data);
                        }
                    })
                    .catch(reject);
            });
        },
        [language, customGeneralTab, t],
    );

    const onEditStandardField = useCallback(
        (row, entities) => {
            const entity = state?.activeTab || state?.activeShrinkedTab?.value;
            setActiveRowId(row.id);

            // Load parent entity data if needed for dynamic fields
            const parentEntity = PARENT_ENTITY_MAP[entity];
            if (parentEntity && !state.fieldsData[parentEntity]) {
                getEntityData(parentEntity).then(() => {
                    // setTimeout allows React to process state update before opening
                    setTimeout(() => {
                        formStandardField.current.open({ idDb: row.idDb, entities, entity });
                    }, 0);
                });
            } else {
                formStandardField.current.open({ idDb: row.idDb, entities, entity });
            }
        },
        [state, getEntityData],
    );

    const onCreateExtraField = useCallback(
        (entities) => {
            const entity = state.activeTab || state.activeShrinkedTab?.value;

            // Load parent entity data if needed for dynamic fields
            const parentEntity = PARENT_ENTITY_MAP[entity];
            if (parentEntity && !state.fieldsData[parentEntity]) {
                getEntityData(parentEntity).then(() => {
                    // setTimeout allows React to process state update before opening
                    setTimeout(() => {
                        formExtraField.current.open({
                            entities,
                            entity,
                        });
                    }, 0);
                });
            } else {
                formExtraField.current.open({
                    entities,
                    entity,
                });
            }
        },
        [state.activeShrinkedTab, state.activeTab, state.fieldsData, getEntityData],
    );

    const onEditExtraField = useCallback(
        (row, entities, tabs, entity, duplicate) => {
            setActiveRowId(row.id);

            // Load parent entity data if needed for dynamic fields
            const parentEntity = PARENT_ENTITY_MAP[entity];
            if (parentEntity && !state.fieldsData[parentEntity]) {
                getEntityData(parentEntity).then(() => {
                    // setTimeout allows React to process state update before opening
                    setTimeout(() => {
                        formExtraField.current.open({
                            id: row.idDb,
                            entities,
                            tabs,
                            entity,
                            duplicate,
                        });
                    }, 0);
                });
            } else {
                formExtraField.current.open({
                    id: row.idDb,
                    entities,
                    tabs,
                    entity,
                    duplicate,
                });
            }
        },
        [state.fieldsData, getEntityData],
    );

    const onCreateFieldsTab = useCallback(
        (entities) => {
            formTab.current.open({ entities, entity });
        },
        [entity],
    );

    const onEditFieldsTab = useCallback(
        (row, entities) => {
            setActiveRowId(row.id);
            const data = state.fieldsData[entity].fields.tab.find((el) => el.id === row.idDb);
            formTab.current.open({
                data,
                entities,
                entity: entity,
            });
        },
        [entity, state.fieldsData],
    );
    const onReorderTabs = useCallback(
        (entityName, row) => {
            setActiveRowId(row.id);
            dispatch({
                type: 'SET_TABS_MODAL',
                data: { isOpen: true, order: state.fieldsData?.[entity]?.tabsOrder },
            });
        },
        [entity, state.fieldsData],
    );

    const onReorderExtraFields = useCallback(
        (tabId, extraOrder) => {
            dispatch({
                type: 'SET_EXTRA_ORDER',
                entity,
                tabId,
                extraOrder,
            });
        },
        [entity],
    );

    const onCloseTabsModal = useCallback(() => {
        setActiveRowId(null);
        dispatch({
            type: 'SET_TABS_MODAL',
            data: { isOpen: false, order: [] },
        });
    }, []);

    const onConfirmTabsModal = useCallback(() => {
        logEvent({
            event: entityLogName,
            submodule: state.entityLogSubmodule,
            functionality: 'sortTab',
        });

        const tabsOrder = state.tabsModal?.order?.map((el, i) => ({ id: el, order: i }));
        updateTabsOrder(tabsOrder)
            .then(() => {
                setActiveRowId(null);
                dispatch({
                    type: 'SET_TABS_ORDER',
                    entity,
                    tabsOrder,
                });
                publish(FIELDS_REFRESH);
            })
            .catch((err) => {
                console.warn(err);
            });
    }, [entity, logEvent, state.entityLogSubmodule, state.tabsModal?.order]);

    const onSetTabsOrder = useCallback((order) => {
        dispatch({
            type: 'SET_TABS_MODAL',
            data: { isOpen: true, order },
        });
    }, []);

    const refreshList = useCallback(() => {
        publish(FIELDS_REFRESH);
        onUpdate && onUpdate();
    }, [onUpdate]);

    const processDataBeforeSave = useCallback((data) => {
        if (!data || Object.keys(data)?.length === 0) return data;
        return Object.entries(data).reduce((obj, [key, value]) => {
            if (key === 'customValidation' && !value) {
                obj[key] = -1;
            } else {
                obj[key] = value;
            }
            return obj;
        }, {});
    }, []);

    const onOpenUpgradeTabsInfo = useCallback(() => {
        dispatch({ type: 'SET_UPGRADE_INFO_TABS', value: true });
        setTimeout(() => {
            document.dispatchEvent(new Event('click'));
        });
    }, []);

    const onCloseUpgradeTabsInfo = useCallback(() => {
        dispatch({ type: 'SET_UPGRADE_INFO_TABS', value: false });
    }, []);

    const onConfirmUpgradeTabsInfo = useCallback(() => {
        handleUpgradePlan && handleUpgradePlan();
        dispatch({ type: 'SET_UPGRADE_INFO_TABS', value: false });
    }, [handleUpgradePlan]);

    const onOpenUpgradeAdminInfo = useCallback(() => {
        dispatch({ type: 'SET_UPGRADE_INFO_ADMIN', value: true });
    }, []);

    const onCloseUpgradeAdminInfo = useCallback(() => {
        dispatch({ type: 'SET_UPGRADE_INFO_ADMIN', value: false });
    }, []);

    const onConfirmUpgradeAdminInfo = useCallback(() => {
        handleUpgradePlan && handleUpgradePlan();
        dispatch({ type: 'SET_UPGRADE_INFO_ADMIN', value: false });
    }, [handleUpgradePlan]);

    const upgradeTabsProps = useMemo(
        () => ({
            title: t('label_upgrade_to_create_new_tabs'),
            content: t('label_upgrade_to_create_new_tabs_desc'),
            videoSrc: t('label_upgrade_to_create_new_tabs_video'),
        }),
        [t],
    );

    const upgradeToAdminProps = useMemo(
        () => ({
            title: t('label_upgrade_to_admin'),
            content: t('label_upgrade_to_admin_desc'),
            videoSrc: t('label_upgrade_to_admin_video'),
        }),
        [t],
    );

    useEffect(() => {
        overrideActiveTab && onChangeTab(overrideActiveTab);
    }, [onChangeTab, overrideActiveTab]);

    return (
        <TransitionGroup>
            <FadeTransition transitionKey="field-list" show={true}>
                <FieldsLists
                    entity={entity}
                    branches={state.branches}
                    onChangeTab={onChangeTab}
                    onChangeShrinked={onChangeShrinked}
                    activeShrinkedTab={state.activeShrinkedTab}
                    activeTab={state.activeTab}
                    onEditStandardField={onEditStandardField}
                    onCreateExtraField={onCreateExtraField}
                    onEditExtraField={onEditExtraField}
                    onCreateFieldsTab={onCreateFieldsTab}
                    onEditFieldsTab={onEditFieldsTab}
                    onReorderTabs={onReorderTabs}
                    fieldsData={state.fieldsData}
                    entityData={state.fieldsData?.[entity]}
                    getEntityData={getEntityData}
                    onReorderExtraFields={onReorderExtraFields}
                    idSetupRole={idSetupRole}
                    activeRowId={activeRowId}
                    setActiveRowId={setActiveRowId}
                    entityLogName={entityLogName}
                    entityLogSubmodule={state.entityLogSubmodule}
                    overrides={overrides}
                    onUpdate={onUpdate}
                    onOpenUpgradeTabsInfo={onOpenUpgradeTabsInfo}
                />
            </FadeTransition>
            <FormStandardField
                onRef={(ref) => (formStandardField.current = ref)}
                refreshList={refreshList}
                setActiveRowId={setActiveRowId}
                fieldsData={state.fieldsData}
                idSetupRole={idSetupRole}
                processDataBeforeSave={processDataBeforeSave}
                entityLogName={entityLogName}
                entityLogSubmodule={state.entityLogSubmodule}
                overrides={overrides}
                onOpenUpgradeAdminInfo={onOpenUpgradeAdminInfo}
                isSage={productCodeIsSage}
            />
            <FormExtraField
                onRef={(ref) => (formExtraField.current = ref)}
                refreshList={refreshList}
                fieldsData={state.fieldsData}
                getEntityData={getEntityData}
                idSetupRole={idSetupRole}
                setActiveRowId={setActiveRowId}
                processDataBeforeSave={processDataBeforeSave}
                entityLogName={entityLogName}
                entityLogSubmodule={state.entityLogSubmodule}
                onOpenUpgradeTabsInfo={onOpenUpgradeTabsInfo}
                onOpenUpgradeAdminInfo={onOpenUpgradeAdminInfo}
                entity={entity}
                overrides={overrides}
                isSage={productCodeIsSage}
            />
            <FormTab
                onRef={(ref) => (formTab.current = ref)}
                refreshList={refreshList}
                fieldsData={state.fieldsData}
                getEntityData={getEntityData}
                idSetupRole={idSetupRole}
                setActiveRowId={setActiveRowId}
                overrides={{
                    ...overrides,
                    fieldsToHide:
                        overrides?.isWeb && overrides?.formTabFieldsToHide?.length
                            ? overrides.formTabFieldsToHide
                            : [],
                }}
            />
            <Modal
                title={t('cfm_label_sort_tabs')}
                isOpen={state.tabsModal.isOpen}
                onRequestClose={onCloseTabsModal}
                onCancel={onCloseTabsModal}
                onConfirm={onConfirmTabsModal}
                cancelText={t('cfm_action_close')}
                confirmText={t('cfm_action_save')}
            >
                <TabsLists
                    list={state.fieldsData?.[entity]?.fields?.tab}
                    order={state.tabsModal?.order}
                    setOrder={onSetTabsOrder}
                    overrides={overrides}
                />
            </Modal>
            <AddOnInfo
                isOpen={state.isUpgradeInfoTabsOpen}
                onRequestClose={onCloseUpgradeTabsInfo}
                onCancel={onCloseUpgradeTabsInfo}
                onConfirm={licenseEditionCodeIsSage ? null : onConfirmUpgradeTabsInfo}
                cancelText={t('action_cancel')}
                confirmText={licenseEditionCodeIsSage ? null : t('action_upgrade')}
                {...upgradeTabsProps}
            />
            {isWeb && (
                <AddOnInfo
                    isOpen={state.isUpgradeInfoAdminOpen}
                    onRequestClose={onCloseUpgradeAdminInfo}
                    onCancel={onCloseUpgradeAdminInfo}
                    onConfirm={licenseEditionCodeIsSage ? null : onConfirmUpgradeAdminInfo}
                    cancelText={t('action_cancel')}
                    confirmText={licenseEditionCodeIsSage ? null : t('action_upgrade')}
                    {...upgradeToAdminProps}
                />
            )}
        </TransitionGroup>
    );
};

Fields.propTypes = {};

export default memo(Fields);
